/**
 * Export project to CodeSandbox using the define API
 * https://codesandbox.io/docs/importing#define-api
 */

type SandboxFile = {
  content: string;
  isBinary?: boolean;
};

type SandboxFiles = {
  [path: string]: SandboxFile;
};

/**
 * Creates CodeSandbox parameters from project files
 */
function createSandboxParameters(files: Record<string, string>): string {
  const sandboxFiles: SandboxFiles = {};

  // Convert our file structure to CodeSandbox format
  for (const [path, content] of Object.entries(files)) {
    // Remove leading slash if present
    const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
    sandboxFiles[normalizedPath] = { content };
  }

  // Ensure we have a package.json
  if (!sandboxFiles['package.json']) {
    sandboxFiles['package.json'] = {
      content: JSON.stringify({
        name: 'exported-project',
        version: '1.0.0',
        main: 'index.js',
        dependencies: {
          react: '^18.2.0',
          'react-dom': '^18.2.0',
        },
        devDependencies: {
          typescript: '^5.0.0',
        },
        scripts: {
          start: 'react-scripts start',
          build: 'react-scripts build',
        },
      }, null, 2),
    };
  }

  // Use the codesandbox-import-utils to generate parameters
  // Since we don't have the library, we'll do it manually
  return compressFiles(sandboxFiles);
}

/**
 * Compress files for CodeSandbox define API
 * This is a simplified version - the actual implementation uses LZString
 */
function compressFiles(files: SandboxFiles): string {
  const filesJson = JSON.stringify({ files });
  // Base64 encode for URL safety
  return btoa(unescape(encodeURIComponent(filesJson)));
}

/**
 * Open CodeSandbox with the project files
 */
export function openInCodeSandbox(files: Record<string, string>): void {
  const sandboxFiles: Record<string, { content: string }> = {};

  // Convert files to CodeSandbox format
  for (const [path, content] of Object.entries(files)) {
    const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
    sandboxFiles[normalizedPath] = { content };
  }

  // Ensure we have a package.json
  if (!sandboxFiles['package.json']) {
    sandboxFiles['package.json'] = {
      content: JSON.stringify({
        name: 'exported-project',
        version: '1.0.0',
        main: 'src/index.tsx',
        dependencies: {
          react: '^18.2.0',
          'react-dom': '^18.2.0',
        },
        devDependencies: {
          '@types/react': '^18.2.0',
          '@types/react-dom': '^18.2.0',
          typescript: '^5.0.0',
        },
        scripts: {
          start: 'react-scripts start',
          build: 'react-scripts build',
        },
      }, null, 2),
    };
  }

  // Create form and submit to CodeSandbox
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = 'https://codesandbox.io/api/v1/sandboxes/define';
  form.target = '_blank';

  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = 'parameters';
  input.value = getCodeSandboxParameters(sandboxFiles);

  form.appendChild(input);
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
}

/**
 * Generate CodeSandbox parameters using LZ-String compression
 */
function getCodeSandboxParameters(files: Record<string, { content: string }>): string {
  // Use the codesandbox define API format
  const payload = { files };

  // LZ-String compression (simplified base64 for now)
  const json = JSON.stringify(payload);

  // Use the actual LZ compression algorithm
  return lzCompress(json);
}

/**
 * LZ-String compression for CodeSandbox
 * Based on lz-string library
 */
function lzCompress(input: string): string {
  if (!input) return '';

  const keyStrBase64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
  const keyStrUriSafe = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+-$';

  function compressToBase64(input: string): string {
    if (!input) return '';
    const res = _compress(input, 6, (a: number) => keyStrBase64.charAt(a));
    switch (res.length % 4) {
      case 0: return res;
      case 1: return res + '===';
      case 2: return res + '==';
      case 3: return res + '=';
      default: return res;
    }
  }

  function _compress(uncompressed: string, bitsPerChar: number, getCharFromInt: (a: number) => string): string {
    let i: number;
    let value: number;
    const context_dictionary: Record<string, number> = {};
    const context_dictionaryToCreate: Record<string, boolean> = {};
    let context_c = '';
    let context_wc = '';
    let context_w = '';
    let context_enlargeIn = 2;
    let context_dictSize = 3;
    let context_numBits = 2;
    let context_data: string[] = [];
    let context_data_val = 0;
    let context_data_position = 0;

    for (let ii = 0; ii < uncompressed.length; ii++) {
      context_c = uncompressed.charAt(ii);
      if (!Object.prototype.hasOwnProperty.call(context_dictionary, context_c)) {
        context_dictionary[context_c] = context_dictSize++;
        context_dictionaryToCreate[context_c] = true;
      }

      context_wc = context_w + context_c;
      if (Object.prototype.hasOwnProperty.call(context_dictionary, context_wc)) {
        context_w = context_wc;
      } else {
        if (Object.prototype.hasOwnProperty.call(context_dictionaryToCreate, context_w)) {
          if (context_w.charCodeAt(0) < 256) {
            for (i = 0; i < context_numBits; i++) {
              context_data_val = (context_data_val << 1);
              if (context_data_position === bitsPerChar - 1) {
                context_data_position = 0;
                context_data.push(getCharFromInt(context_data_val));
                context_data_val = 0;
              } else {
                context_data_position++;
              }
            }
            value = context_w.charCodeAt(0);
            for (i = 0; i < 8; i++) {
              context_data_val = (context_data_val << 1) | (value & 1);
              if (context_data_position === bitsPerChar - 1) {
                context_data_position = 0;
                context_data.push(getCharFromInt(context_data_val));
                context_data_val = 0;
              } else {
                context_data_position++;
              }
              value = value >> 1;
            }
          } else {
            value = 1;
            for (i = 0; i < context_numBits; i++) {
              context_data_val = (context_data_val << 1) | value;
              if (context_data_position === bitsPerChar - 1) {
                context_data_position = 0;
                context_data.push(getCharFromInt(context_data_val));
                context_data_val = 0;
              } else {
                context_data_position++;
              }
              value = 0;
            }
            value = context_w.charCodeAt(0);
            for (i = 0; i < 16; i++) {
              context_data_val = (context_data_val << 1) | (value & 1);
              if (context_data_position === bitsPerChar - 1) {
                context_data_position = 0;
                context_data.push(getCharFromInt(context_data_val));
                context_data_val = 0;
              } else {
                context_data_position++;
              }
              value = value >> 1;
            }
          }
          context_enlargeIn--;
          if (context_enlargeIn === 0) {
            context_enlargeIn = Math.pow(2, context_numBits);
            context_numBits++;
          }
          delete context_dictionaryToCreate[context_w];
        } else {
          value = context_dictionary[context_w];
          for (i = 0; i < context_numBits; i++) {
            context_data_val = (context_data_val << 1) | (value & 1);
            if (context_data_position === bitsPerChar - 1) {
              context_data_position = 0;
              context_data.push(getCharFromInt(context_data_val));
              context_data_val = 0;
            } else {
              context_data_position++;
            }
            value = value >> 1;
          }
        }
        context_enlargeIn--;
        if (context_enlargeIn === 0) {
          context_enlargeIn = Math.pow(2, context_numBits);
          context_numBits++;
        }
        context_dictionary[context_wc] = context_dictSize++;
        context_w = String(context_c);
      }
    }

    if (context_w !== '') {
      if (Object.prototype.hasOwnProperty.call(context_dictionaryToCreate, context_w)) {
        if (context_w.charCodeAt(0) < 256) {
          for (i = 0; i < context_numBits; i++) {
            context_data_val = (context_data_val << 1);
            if (context_data_position === bitsPerChar - 1) {
              context_data_position = 0;
              context_data.push(getCharFromInt(context_data_val));
              context_data_val = 0;
            } else {
              context_data_position++;
            }
          }
          value = context_w.charCodeAt(0);
          for (i = 0; i < 8; i++) {
            context_data_val = (context_data_val << 1) | (value & 1);
            if (context_data_position === bitsPerChar - 1) {
              context_data_position = 0;
              context_data.push(getCharFromInt(context_data_val));
              context_data_val = 0;
            } else {
              context_data_position++;
            }
            value = value >> 1;
          }
        } else {
          value = 1;
          for (i = 0; i < context_numBits; i++) {
            context_data_val = (context_data_val << 1) | value;
            if (context_data_position === bitsPerChar - 1) {
              context_data_position = 0;
              context_data.push(getCharFromInt(context_data_val));
              context_data_val = 0;
            } else {
              context_data_position++;
            }
            value = 0;
          }
          value = context_w.charCodeAt(0);
          for (i = 0; i < 16; i++) {
            context_data_val = (context_data_val << 1) | (value & 1);
            if (context_data_position === bitsPerChar - 1) {
              context_data_position = 0;
              context_data.push(getCharFromInt(context_data_val));
              context_data_val = 0;
            } else {
              context_data_position++;
            }
            value = value >> 1;
          }
        }
        context_enlargeIn--;
        if (context_enlargeIn === 0) {
          context_enlargeIn = Math.pow(2, context_numBits);
          context_numBits++;
        }
        delete context_dictionaryToCreate[context_w];
      } else {
        value = context_dictionary[context_w];
        for (i = 0; i < context_numBits; i++) {
          context_data_val = (context_data_val << 1) | (value & 1);
          if (context_data_position === bitsPerChar - 1) {
            context_data_position = 0;
            context_data.push(getCharFromInt(context_data_val));
            context_data_val = 0;
          } else {
            context_data_position++;
          }
          value = value >> 1;
        }
      }
      context_enlargeIn--;
      if (context_enlargeIn === 0) {
        context_enlargeIn = Math.pow(2, context_numBits);
        context_numBits++;
      }
    }

    value = 2;
    for (i = 0; i < context_numBits; i++) {
      context_data_val = (context_data_val << 1) | (value & 1);
      if (context_data_position === bitsPerChar - 1) {
        context_data_position = 0;
        context_data.push(getCharFromInt(context_data_val));
        context_data_val = 0;
      } else {
        context_data_position++;
      }
      value = value >> 1;
    }

    while (true) {
      context_data_val = (context_data_val << 1);
      if (context_data_position === bitsPerChar - 1) {
        context_data.push(getCharFromInt(context_data_val));
        break;
      } else {
        context_data_position++;
      }
    }

    return context_data.join('');
  }

  return compressToBase64(input);
}

/**
 * Open project in StackBlitz
 */
export function openInStackBlitz(files: Record<string, string>, projectName: string = 'project'): void {
  const stackblitzFiles: Record<string, string> = {};

  // Convert files to StackBlitz format
  for (const [path, content] of Object.entries(files)) {
    const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
    stackblitzFiles[normalizedPath] = content;
  }

  // Ensure we have a package.json
  if (!stackblitzFiles['package.json']) {
    stackblitzFiles['package.json'] = JSON.stringify({
      name: projectName,
      version: '1.0.0',
      main: 'src/index.tsx',
      dependencies: {
        react: '^18.2.0',
        'react-dom': '^18.2.0',
      },
      devDependencies: {
        '@types/react': '^18.2.0',
        '@types/react-dom': '^18.2.0',
        typescript: '^5.0.0',
      },
      scripts: {
        dev: 'vite',
        build: 'vite build',
      },
    }, null, 2);
  }

  // Create form for StackBlitz
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = 'https://stackblitz.com/run';
  form.target = '_blank';

  // Add project files
  for (const [path, content] of Object.entries(stackblitzFiles)) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = `project[files][${path}]`;
    input.value = content;
    form.appendChild(input);
  }

  // Add project title
  const titleInput = document.createElement('input');
  titleInput.type = 'hidden';
  titleInput.name = 'project[title]';
  titleInput.value = projectName;
  form.appendChild(titleInput);

  // Add template
  const templateInput = document.createElement('input');
  templateInput.type = 'hidden';
  templateInput.name = 'project[template]';
  templateInput.value = 'node';
  form.appendChild(templateInput);

  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
}
